import type { AuthSession } from "./models/session.model";

const PLACEHOLDER_TENANT_NAMES = new Set(["ITaaS Demo", "ITaaS"]);

function decodeJwtPayload(token: string): Record<string, unknown> {
  const segment = token.split(".")[1];
  if (!segment) return {};
  const normalized = segment.replace(/-/g, "+").replace(/_/g, "/");
  return JSON.parse(atob(normalized)) as Record<string, unknown>;
}

/** Nom établissement depuis session IAM ou claim JWT tenantName. */
export function resolveIamTenantName(session: AuthSession | null | undefined): string | undefined {
  if (!session) return undefined;

  const fromJwt = (() => {
    try {
      const token = session.tokens?.accessToken;
      if (!token) return undefined;
      const payload = decodeJwtPayload(token);
      const name = typeof payload.tenantName === "string" ? payload.tenantName.trim() : "";
      return name || undefined;
    } catch {
      return undefined;
    }
  })();

  for (const candidate of [session.tenant?.name, fromJwt]) {
    const trimmed = candidate?.trim();
    if (trimmed && !PLACEHOLDER_TENANT_NAMES.has(trimmed)) return trimmed;
  }

  return undefined;
}

export interface TenantProfileSyncOptions<T extends { name: string }> {
  getProfile: () => T;
  saveProfile: (profile: T) => void;
  defaultProfile: T;
  isDefaultProfile?: (profile: T) => boolean;
}

/**
 * Aligne un profil local (établissement) sur le nom IAM si encore au défaut démo.
 * À appeler après handoff SSO ou verifySession dans chaque module web.
 */
export function syncTenantProfileFromSession<T extends { name: string }>(
  session: AuthSession | null | undefined,
  options: TenantProfileSyncOptions<T>,
): void {
  const tenantName = resolveIamTenantName(session);
  const tenantSlug = session?.tenant?.slug?.trim().toLowerCase();
  if (!tenantName || !tenantSlug) return;

  const current = options.getProfile();
  const isDefault = options.isDefaultProfile
    ? options.isDefaultProfile(current)
    : current.name.trim() === options.defaultProfile.name.trim();

  if (!isDefault) return;

  options.saveProfile({ ...current, name: tenantName });
}
